import swell from 'swell-js';
import { STORE } from './store';

const STORE_ID = process.env.REACT_APP_SWELL_STORE_ID || 'p0p-store';
const PUBLIC_KEY = process.env.REACT_APP_SWELL_PUBLIC_KEY || 'pk_HdqCctkpFEIWSYwEDQH02zHJtgvCapVW';

// Initialize swell-js (used for cart operations and local dev fallback)
swell.init(STORE_ID, PUBLIC_KEY);

// Client-side price cache: product_id -> { price (list), sale_price }
// Populated whenever products are fetched so cart items can show sale strikethrough
const priceCache = new Map();

function normalizeProduct(p) {
  if (!p) return null;
  const images = p.images || [];
  const imageUrls = images
    .filter(img => img?.file?.url)
    .map(img => img.file.url);
  // When a Swell sale is active: price = sale price, orig_price = original list price
  // When no sale: price = list price, orig_price = null
  // purchase_options.standard.price is also the list price (admin API)
  const rawPrice = p.price || 0;
  const origPrice = p.orig_price || p.purchase_options?.standard?.price || null;
  const rawSalePrice = p.sale_price || p.purchase_options?.standard?.sale_price || null;

  let listPrice, salePrice;
  if (origPrice && origPrice > rawPrice) {
    // Sale is active: orig_price = list, price = sale
    listPrice = origPrice;
    salePrice = rawPrice;
  } else if (rawSalePrice && rawSalePrice > 0 && rawSalePrice < rawPrice) {
    // Explicit sale_price lower than price
    listPrice = rawPrice;
    salePrice = rawSalePrice;
  } else {
    // No sale
    listPrice = origPrice || rawPrice;
    salePrice = null;
  }

  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    description: p.description || '',
    price: listPrice,
    sale_price: salePrice,
    currency: p.currency || 'USD',
    image_url: imageUrls[0] || null,
    images: imageUrls,
    active: p.active !== false,
    options: p.options || [],
    tags: p.tags || [],
    variants: Array.isArray(p.variants?.results)
      ? p.variants.results.map(v => ({
          id: v.id,
          name: v.name,
          price: v.price,
          option_value_ids: v.option_value_ids || [],
          stock_status: v.stock_status,
        }))
      : [],
    category_ids: Array.isArray(p.category_index?.id) ? p.category_index.id : [],
  };
}

function normalizeCart(data) {
  if (!data) return null;
  const items = (data.items || []).map(item => {
    const product = item.product || {};
    const images = product.images || [];
    const imageUrl = images[0]?.file?.url || null;
    const itemPrice = item.price || 0;
    // Look up list price from cache (populated when products were fetched)
    const cached = priceCache.get(item.product_id);
    const candidateOrig = item.orig_price
      || product.orig_price
      || (cached?.sale_price ? cached.price : null)
      || product.purchase_options?.standard?.price
      || null;
    const itemOrigPrice = (candidateOrig && candidateOrig > itemPrice) ? candidateOrig : null;
    return {
      id: item.id,
      product_id: item.product_id,
      product_name: product.name || '',
      quantity: item.quantity || 1,
      price: itemPrice,
      orig_price: itemOrigPrice,
      price_total: item.price_total || 0,
      image_url: imageUrl,
      options: item.options || [],
      variant_id: item.variant_id || null,
    };
  });
  return {
    id: data.id,
    items,
    sub_total: data.sub_total || 0,
    grand_total: data.grand_total || 0,
    item_quantity: data.item_quantity || 0,
    currency: data.currency || 'USD',
    checkout_url: data.checkout_url || null,
  };
}

export const swellProducts = {
  async list(params = {}) {
    // In production, use the Cloudflare Pages Function which calls
    // the Swell admin API with the secret key (more reliable, no auth issues)
    try {
      const queryParams = new URLSearchParams();
      if (params.brand) queryParams.set('brand', params.brand);
      if (params.featured !== undefined) queryParams.set('featured', String(params.featured));
      if (params.category) queryParams.set('category', params.category);
      queryParams.set('store', STORE);
      const url = `/api/products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const res = await fetch(url, { signal: AbortSignal.timeout(10000) });

      if (res.ok) {
        const raw = await res.json();
        const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.results) ? raw.results : null);
        if (arr && arr.length > 0) {
          const products = arr.map(p => { try { return normalizeProduct(p); } catch { return null; } }).filter(Boolean);
          products.forEach(p => p.id && priceCache.set(p.id, { price: p.price, sale_price: p.sale_price }));
          return { products, count: products.length, page: 1, pages: 1 };
        }
      }
    } catch {
      // CF Function not available (local dev) — fall through to SDK
    }

    // Fallback: use swell-js SDK directly (local development)
    try {
      const response = await swell.products.list({
        limit: params.limit || 100,
        page: params.page || 1,
        expand: ['variants'],
      });
      const products = (response?.results || []).map(normalizeProduct).filter(Boolean);
      products.forEach(p => p.id && priceCache.set(p.id, { price: p.price, sale_price: p.sale_price }));
      const pages = response?.pages ? Object.keys(response.pages).length : 1;
      return { products, count: response?.count || 0, page: params.page || 1, pages };
    } catch (err) {
      console.error('[Swell] Failed to fetch products:', err);
      return { products: [], count: 0, page: 1, pages: 1 };
    }
  },

  async get(id) {
    // Try CF Function first (admin API — purchase_options has correct list/sale prices)
    try {
      const res = await fetch(`/api/products/${id}`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) {
        const raw = await res.json();
        if (raw?.id) {
          const product = normalizeProduct(raw);
          if (product) priceCache.set(product.id, { price: product.price, sale_price: product.sale_price });
          return product;
        }
      }
    } catch {
      // CF Function unavailable (local dev) — fall through to SDK
    }

    // Fallback: swell-js SDK
    try {
      const p = await swell.products.get(id, { expand: ['variants'] });
      return normalizeProduct(p);
    } catch (err) {
      console.error('[Swell] Failed to fetch product:', err);
      return null;
    }
  },
};

export const swellCategories = {
  async list(params = {}) {
    try {
      const data = await swell.categories.list({ limit: 100, ...params });
      const categories = (data?.results || []).map(cat => ({
        id: cat.id,
        name: cat.name,
        slug: cat.slug,
        description: cat.description || '',
        parent_id: cat.parent_id || null,
        image_url: cat.images?.[0]?.file?.url || null,
        product_count: cat.product_count || 0,
      }));
      return { categories, count: data?.count || 0 };
    } catch (err) {
      console.error('[Swell] Failed to fetch categories:', err);
      return { categories: [], count: 0 };
    }
  },
};

export const swellCart = {
  async get() {
    try {
      const data = await swell.cart.get({ expand: ['items.product'] });
      return normalizeCart(data);
    } catch (err) {
      console.error('[Swell] Failed to get cart:', err);
      return null;
    }
  },

  async addItem(productId, quantity = 1, options = null, variantId = null) {
    try {
      const item = { product_id: productId, quantity };
      if (options) item.options = options;
      if (variantId) item.variant_id = variantId;
      const data = await swell.cart.addItem(item, { expand: ['items.product'] });
      return normalizeCart(data);
    } catch (err) {
      console.error('[Swell] Failed to add item:', err);
      return null;
    }
  },

  async removeItem(itemId) {
    try {
      const data = await swell.cart.removeItem(itemId);
      return normalizeCart(data);
    } catch (err) {
      console.error('[Swell] Failed to remove item:', err);
      return null;
    }
  },

  async updateItem(itemId, quantity) {
    try {
      const data = await swell.cart.updateItem(itemId, { quantity }, { expand: ['items.product'] });
      return normalizeCart(data);
    } catch (err) {
      console.error('[Swell] Failed to update item:', err);
      return null;
    }
  },
};
